import { useEffect, useMemo, useState } from 'react';
import { MdOutlineWarningAmber } from 'react-icons/md';
import * as Dfns from 'date-fns/fp';
import { numberFormatter, snapShotDataSort } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { LineChart } from '@Components/Charts/LineChart/LineChart';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { EmptyState } from '@Components/EmptyState';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { Page } from '@Components/Page/Page';
import { SnapshotChart } from '@Components/SnapshotChart/SnapshotChart';
import {
  DataItem,
  SNAPSHOT_SORT_OPTIONS
} from '@Components/SnapshotChart/snapshot-chart-constants';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { dateToMddyy, DateRange } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { EMQScores } from './signal-dashboard-constants';
import { useEmqScores } from './use-signal-dashboard';
import './SignalDashboard.scss';

interface EventLine {
  dataKey: string;
  name: string;
}

interface DailyPanelProps {
  company: string;
  events: EventLine[];
  filteredEmqScores: EMQScores[];
}

const DailyPanel = ({ company, events, filteredEmqScores }: DailyPanelProps) => {
  const [graphView, setGraphView] = useState(true);

  const tableHeaders = useMemo<TypedHeader<EMQScores>[]>(
    () => [
      { field: 'date', label: 'Date', renderer: row => dateToMddyy(row.date) },
      ...events.map(event => ({
        field: event.dataKey,
        label: event.name,
        renderer: (row: EMQScores) => {
          const value = row[event.dataKey];
          return typeof value === 'number' ? numberFormatter.format(value, 1) : '--';
        }
      }))
    ],
    [events]
  );

  const pngDownload = async () => {
    await downloadPNG('.signal-dashboard-chart-container', 'Signal Dashboard');
  };

  const excelDownload = () => {
    exportToExcel(filteredEmqScores, `${company}_emq_scores`);
  };

  return (
    <div className='signal-dashboard-daily-panel' data-testid='signal-dashboard-daily-chart'>
      <ChartContainer
        className='signal-dashboard-chart-container'
        title='Daily EMQ Scores'
        viewRightActions
        rightActions={
          <div className='signal-dashboard-right-actions'>
            <GraphTableToggleButton
              graphView={graphView}
              setGraphView={setGraphView}
              widgetName='Meta Signal Dashboard'
              size={Size.MD}
            />
            <div data-testid='download-dropdown'>
              <DownloadDropdown
                menuOptions={['PNG', 'XLSX']}
                onClickOptions={[pngDownload, excelDownload]}
                disabledMenuOptions={{
                  PNG: !graphView
                }}
              />
            </div>
          </div>
        }
      >
        {graphView && (
          <LineChart
            data={filteredEmqScores}
            customLeftYAxisDomain={[0, 10]}
            lineDataKeys={events}
            xAxisDataKey='date'
            useLeftYAxis
            tooltipShape='line'
            customTooltipHeader={val => val}
            tooltipFormatter={value => `${numberFormatter.format(value, 1)}/10`}
            xAxisTickFormatter={date => dateToMddyy(date)}
          />
        )}
        {!graphView && (
          <div className='signal-dashboard-table-wrapper' data-testid='signal-dashboard-table'>
            <BPMTable headers={tableHeaders} data={filteredEmqScores} />
          </div>
        )}
      </ChartContainer>
    </div>
  );
};

interface BodyProps {
  company: string;
  isLoading: boolean;
  hasError: boolean;
  events?: EventLine[];
  filteredEmqScores: EMQScores[];
}

const SignalDashboardBody = ({
  company,
  isLoading,
  hasError,
  events,
  filteredEmqScores
}: BodyProps) => {
  const [snapChartSortValue, setSnapChartSortValue] = useState('Highest to lowest');

  const avgEmqScores = useMemo<DataItem[]>(() => {
    const sumMap: Record<string, { total: number; count: number }> = {};
    filteredEmqScores.forEach(item => {
      Object.entries(item).forEach(([key, value]) => {
        if (key !== 'date' && typeof value === 'number' && !Number.isNaN(value)) {
          sumMap[key] ??= { total: 0, count: 0 };
          sumMap[key].total += value;
          sumMap[key].count += 1;
        }
      });
    });

    return Object.entries(sumMap).map(([event, { total, count }]) => {
      const average = total / count;
      return { name: event, value: average, label: numberFormatter.format(average, 1) };
    });
  }, [filteredEmqScores]);

  if (isLoading) return <FullPageSpinner />;

  if (!events) {
    return (
      <EmptyState
        description={
          hasError ? "Couldn't load EMQ data." : 'No EMQ data is available for this company.'
        }
      />
    );
  }

  return (
    <div className='signal-dashboard-page'>
      <WidgetContainer
        fillHeight
        dataTestId='signal-dashboard-widget'
        header='Event Match Quality'
        subHeader={
          <>
            High quality event matching may improve ads attribution and performance. Note that this
            metric is in development by Meta.
            <InfoTooltip rightLabel='More info' size={Size.SM} inline>
              Event Match Quality (EMQ) is a score (out of 10) that indicates how effective the
              customer information sent from your server may be at matching event instances to a
              Facebook account. It&apos;s calculated by looking at which customer information
              parameters are received from your server via a Conversions API integration, the
              quality of the information received, and the percent of event instances that are
              matched to a Meta account.
            </InfoTooltip>
          </>
        }
        footer={
          <div className='signal-dashboard-bottom-wrapper'>
            <MdOutlineWarningAmber className='signal-dashboard-warning-icon' />
            <div>EMQ is currently only available for web events.</div>
          </div>
        }
      >
        <div className='signal-dashboard-container'>
          <div
            className='signal-dashboard-snapshot-panel'
            data-testid='signal-dashboard-snapshot-chart'
          >
            <SnapshotChart
              data={snapShotDataSort(avgEmqScores, snapChartSortValue)}
              customXAxisDomain={[0, 10]}
              title='Average EMQ Scores'
              valueFormatter={value => `${numberFormatter.format(value, 1)}/10`}
              dropdownOptions={SNAPSHOT_SORT_OPTIONS}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
            />
          </div>
          <DailyPanel company={company} events={events} filteredEmqScores={filteredEmqScores} />
        </div>
      </WidgetContainer>
    </div>
  );
};

export const SignalDashboard = () => {
  const company = useAppSelector(currentCompanySelector);
  const setError = useSetError();
  const { data: emqScores, isLoading, error } = useEmqScores(company);

  const [selectedRange, setSelectedRange] = useState<DateRange>();

  // A range picked for one company's data window doesn't carry over to the next.
  useEffect(() => {
    setSelectedRange(undefined);
  }, [company]);

  useEffect(() => {
    if (!error) return;
    setError({
      title: "Couldn't load data",
      message: `Failed to load Meta Signal Dashboard data: ${error.message}`,
      reportError: error
    });
  }, [error, setError]);

  const toDateStr = (date?: string) =>
    date ? Dfns.format('yyyy-MM-dd', Dfns.parseISO(date)) : undefined;
  const minDate = toDateStr(emqScores?.[0]?.date);
  const maxDate = toDateStr(emqScores?.[emqScores.length - 1]?.date);

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (selectedRange) return selectedRange;
    if (!maxDate) return undefined;

    return {
      start: Dfns.format('yyyy-MM-dd', Dfns.subDays(30, Dfns.parseISO(maxDate))),
      end: maxDate
    };
  }, [selectedRange, maxDate]);

  const events = useMemo(() => {
    if (!emqScores?.length) return undefined;

    return Object.keys(emqScores[0])
      .filter(key => key !== 'date')
      .map(key => ({ dataKey: key, name: key }));
  }, [emqScores]);

  const filteredEmqScores = useMemo(() => {
    if (!emqScores || !dateRange) return [];

    return emqScores.filter(({ date }) => {
      const d = Dfns.parseISO(date);
      return d >= Dfns.parseISO(dateRange.start) && d <= Dfns.parseISO(dateRange.end);
    });
  }, [emqScores, dateRange]);

  return (
    <Page
      title='Meta Signal Dashboard'
      pageType='Meta Signal Dashboard'
      isLoading={isLoading}
      actions={
        <DatePicker
          range={dateRange}
          onChange={setSelectedRange}
          maxDate={maxDate}
          minDate={minDate}
          isDayBlocked={date => minDate !== undefined && date < minDate}
          removeHeaderText
          datePresets
        />
      }
    >
      <SignalDashboardBody
        company={company}
        isLoading={isLoading}
        hasError={Boolean(error)}
        events={events}
        filteredEmqScores={filteredEmqScores}
      />
    </Page>
  );
};
